import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import type { Hex } from 'viem'
import { useEIP712Sign } from '@provex/eip712-utils'
import { MAX_SIGNATURE_AGE_MS } from 'sacrifice-utils'

/** Persisted instance-admin sign-in. Not bound to any campaign. */
interface InstanceAuthData {
  signature: Hex
  /** Milliseconds since the Unix epoch; the signed timestamp. */
  timestamp: number
}

export const INSTANCE_AUTH_STORAGE_KEY = 'altar.instanceAuth'

const isFresh = (auth: InstanceAuthData, now: number): boolean =>
  now - auth.timestamp < MAX_SIGNATURE_AGE_MS

const loadFromStorage = (): InstanceAuthData | null => {
  try {
    const raw = localStorage.getItem(INSTANCE_AUTH_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as InstanceAuthData
    if (typeof parsed.signature !== 'string' || typeof parsed.timestamp !== 'number') return null
    if (!isFresh(parsed, Date.now())) return null
    return parsed
  } catch {
    return null
  }
}

export interface InstanceAuthContextValue {
  authData: { signature: Hex; timestamp: bigint } | null
  isSignedIn: boolean
  signIn: () => Promise<void>
  signOut: () => void
}

const InstanceAuthContext = createContext<InstanceAuthContextValue>({
  authData: null,
  isSignedIn: false,
  signIn: async () => {},
  signOut: () => {},
})

export const InstanceAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { signInstanceLogin } = useEIP712Sign()
  const [auth, setAuth] = useState<InstanceAuthData | null>(loadFromStorage)

  const signIn = useCallback(async () => {
    const timestamp = Date.now()
    const signature = await signInstanceLogin({ timestamp: BigInt(timestamp) })
    if (!signature) return
    const next: InstanceAuthData = { signature, timestamp }
    setAuth(next)
    try {
      localStorage.setItem(INSTANCE_AUTH_STORAGE_KEY, JSON.stringify(next))
    } catch {
      // localStorage may be unavailable; in-memory auth still works for the session.
    }
  }, [signInstanceLogin])

  const signOut = useCallback(() => {
    setAuth(null)
    try {
      localStorage.removeItem(INSTANCE_AUTH_STORAGE_KEY)
    } catch {
      // ignore
    }
  }, [])

  const value = useMemo<InstanceAuthContextValue>(() => {
    const fresh = auth && isFresh(auth, Date.now()) ? auth : null
    return {
      authData: fresh ? { signature: fresh.signature, timestamp: BigInt(fresh.timestamp) } : null,
      isSignedIn: fresh !== null,
      signIn,
      signOut,
    }
  }, [auth, signIn, signOut])

  return <InstanceAuthContext.Provider value={value}>{children}</InstanceAuthContext.Provider>
}

export const useInstanceAuth = (): InstanceAuthContextValue => useContext(InstanceAuthContext)
